/** Per-feeder data-quality rollup tab (spec §7 item 7).
 *
 *  One call (`useDataQuality`) composes the relationship-source shares, the
 *  default-impedance %, the derived-graph component structure, device-rating
 *  coverage, verification progress, and the normally-open boundary worklist —
 *  the same numbers each standalone card shows. Replaces the retired legacy
 *  node-string connectivity audit card. */

import { useDataQuality } from '../../hooks/useEngineering.ts'
import type {
  RelationshipSourceShare,
  VerificationSummary,
} from '../../types/dataQuality.ts'
import { friendlyError } from '../../utils/friendlyError.ts'

interface DataQualityTabProps {
  feederId: number | null
}

function fmtPct(n: number, total: number): string {
  if (total <= 0) return '0.0%'
  return `${((n / total) * 100).toFixed(1)}%`
}

// The four relationship_source buckets, in stacked-bar order. `spatial_linking`
// is the token production writes (scripts/spatial_linking.py) — never
// `spatial_inference`. Labels align with RelationshipBadge (UX B2).
const SOURCE_BUCKETS: Array<{
  key: keyof Omit<RelationshipSourceShare, 'total'>
  label: string
  color: string
}> = [
  { key: 'gis_attribute', label: 'GIS record', color: '#3498db' },
  { key: 'manual_assignment', label: 'Manually assigned', color: '#e67e22' },
  { key: 'spatial_linking', label: 'Map position', color: '#9b59b6' },
  { key: 'other', label: 'Other / unknown', color: '#95a5a6' },
]

function RelationshipShareCard({
  title,
  share,
  lead,
  refused,
}: {
  title: string
  share: RelationshipSourceShare
  lead?: boolean
  /** Rows a distance cap refused to link. Not part of the share (they keep
   *  their old parent — decision D2), so it is shown beside the total, never
   *  folded into the bar. */
  refused?: number
}) {
  return (
    <div
      className={`rounded-lg border p-3 ${
        lead ? 'border-blue-300 bg-blue-50/50' : 'border-gray-200 bg-white'
      }`}
    >
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-xs font-semibold text-gray-700">{title}</h4>
        <span className="text-xs text-gray-500">
          {share.total} rows
          {refused ? <span className="ml-1 text-amber-700">· {refused} refused</span> : null}
        </span>
      </div>

      {/* Stacked share bar */}
      <div className="mb-2 flex h-2 w-full overflow-hidden rounded-full bg-gray-100">
        {SOURCE_BUCKETS.map(b =>
          share[b.key] > 0 && share.total > 0 ? (
            <div
              key={b.key}
              style={{ width: fmtPct(share[b.key], share.total), backgroundColor: b.color }}
            />
          ) : null,
        )}
      </div>

      <div className="space-y-0.5">
        {SOURCE_BUCKETS.map(b => (
          <div key={b.key} className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-1.5 text-gray-600">
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ backgroundColor: b.color }}
              />
              {b.label}
            </span>
            <span className="tabular-nums text-gray-700">
              {share[b.key]}{' '}
              <span className="text-gray-400">({fmtPct(share[b.key], share.total)})</span>
            </span>
          </div>
        ))}
      </div>

      {lead && (
        <div className="mt-2 text-[11px] leading-snug text-blue-700">
          Manually assigned meters were re-linked by hand where the meter&apos;s GIS
          record disagreed with its transformer; GIS-record rows come straight
          from the import, untouched.
        </div>
      )}
    </div>
  )
}

function VerificationBars({
  label,
  summary,
}: {
  label: string
  summary: VerificationSummary
}) {
  const verifiedPct = summary.verified_percentage
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-xs">
        <span className="text-gray-600">{label}</span>
        <span className="tabular-nums text-gray-700">
          {summary.verified}/{summary.total} verified ({verifiedPct.toFixed(1)}%)
        </span>
      </div>
      <div className="flex h-2 w-full overflow-hidden rounded-full bg-gray-100">
        <div style={{ width: `${Math.min(100, verifiedPct)}%`, backgroundColor: '#2ecc71' }} />
      </div>
      {(summary.flagged > 0 || summary.needs_review > 0) && (
        <div className="mt-1 flex gap-3 text-[11px] text-gray-500">
          {summary.flagged > 0 && <span>{summary.flagged} flagged</span>}
          {summary.needs_review > 0 && (
            <span className="text-amber-700">{summary.needs_review} needs review</span>
          )}
        </div>
      )}
    </div>
  )
}

const NO_BOUNDARY_ROW_STYLES: Record<string, string> = {
  interior_defect: 'bg-red-50 text-red-800',
  boundary_ok: 'text-gray-700',
  unplaceable: 'bg-gray-50 text-gray-500',
}

export function DataQualityTab({ feederId }: DataQualityTabProps) {
  const { data, isLoading, error } = useDataQuality(feederId)

  if (feederId == null) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-sm text-gray-400">
        Select a feeder to see its data-quality rollup.
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-400">
        Loading data-quality rollup...
      </div>
    )
  }

  // Error state visually distinct from empty (AMI-tab pattern): a failed request
  // must never read as "no data".
  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {friendlyError(error, 'load the data-quality rollup')} This is a request
        error, not an empty feeder.
      </div>
    )
  }

  if (!data) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-400">
        No data-quality rollup available for this feeder.
      </div>
    )
  }

  const nb = data.no_boundary
  const drc = data.device_rating_coverage
  const dg = data.derived_graph
  const dup = data.duplicate_device_names
  const fed = data.fed_by_check
  const le = data.link_evidence

  return (
    <div className="space-y-4">
      {/* Relationship-source shares — the meters card leads (XFMR_ID signal). */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <h3 className="mb-3 text-sm font-semibold text-gray-700">Relationship-source shares</h3>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <RelationshipShareCard title="Meters" share={data.relationship_source_meters} lead />
          <RelationshipShareCard
            title="Transformers"
            share={data.relationship_source_transformers}
          />
          <RelationshipShareCard title="Segments" share={data.relationship_source_segments} />
          {data.relationship_source_devices && (
            <RelationshipShareCard
              title="Devices"
              share={data.relationship_source_devices}
              refused={data.link_refused?.protective_devices}
            />
          )}
        </div>
      </div>

      {/* Numeric tiles: default-impedance %, derived-graph structure, verification. */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {/* Default impedance */}
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <h3 className="mb-2 text-sm font-semibold text-gray-700">Default impedance</h3>
          <div className="text-3xl font-bold text-gray-800">
            {data.default_impedance_pct.toFixed(1)}%
          </div>
          <div
            className="mt-1 text-xs text-gray-500"
            title="No per-segment R/X on record — a standard default impedance is assumed"
          >
            {data.default_impedance_segments} of {data.total_segments} line segments
            have no electrical data on record, so a standard default is assumed.
          </div>
        </div>

        {/* Derived-graph structure */}
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <h3 className="mb-2 text-sm font-semibold text-gray-700">Derived-graph structure</h3>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <div className="text-lg font-bold text-gray-800">{dg.segments}</div>
              <div className="text-[11px] text-gray-500">Segments</div>
            </div>
            <div>
              <div className="text-lg font-bold text-gray-800">{dg.components}</div>
              <div className="text-[11px] text-gray-500">Components</div>
            </div>
            <div>
              <div className="text-lg font-bold text-gray-800">
                {dg.source_reachable_pct.toFixed(1)}%
              </div>
              <div className="text-[11px] text-gray-500">Reaches source</div>
            </div>
          </div>
          <div className="mt-2 text-xs text-gray-500">
            {dg.source_reachable} of {dg.segments} line segments connect back to
            the substation source — segments that don&apos;t usually point to a
            mapping gap.
          </div>
        </div>

        {/* Verification progress */}
        <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-gray-700">Verification progress</h3>
          <VerificationBars label="Meters" summary={data.verification.meters} />
          <VerificationBars label="Transformers" summary={data.verification.transformers} />
        </div>
      </div>

      {/* Device-rating coverage — compact restatement of the nested report. */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700">Device-rating coverage</h3>
          <span className="text-xs text-gray-500">{drc.total_devices} devices</span>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded-lg border border-gray-200 p-3 text-center">
            <div className="text-2xl font-bold text-green-600">{drc.rated}</div>
            <div className="mt-1 text-xs text-gray-500">Rated</div>
          </div>
          <div className="rounded-lg border border-gray-200 p-3 text-center">
            <div className="text-2xl font-bold text-blue-600">{drc.parseable}</div>
            <div className="mt-1 text-xs text-gray-500">Parseable</div>
          </div>
          <div className="rounded-lg border border-gray-200 p-3 text-center">
            <div className="text-2xl font-bold text-red-600">{drc.missing}</div>
            <div className="mt-1 text-xs text-gray-500">Missing</div>
          </div>
          <div className="rounded-lg border border-gray-200 p-3 text-center">
            <div className="text-2xl font-bold text-gray-500">{drc.unplaced}</div>
            <div className="mt-1 text-xs text-gray-500">Unplaced</div>
          </div>
        </div>
      </div>

      {/* Duplicate device names (C8 / D-12) — a GIS cleanup worklist. Tables
          already disambiguate in display; fixing the names in GIS is lasting. */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700">Duplicate device names</h3>
          {dup && dup.duplicate_groups > 0 && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700">
              {dup.duplicate_groups} {dup.duplicate_groups === 1 ? 'name' : 'names'} shared ·{' '}
              {dup.devices_affected} devices
            </span>
          )}
        </div>
        {!dup || dup.duplicate_groups === 0 ? (
          <div className="p-2 text-sm text-gray-400">
            Every named device on this feeder has a unique name.
          </div>
        ) : (
          <>
            <div className="mb-2 text-xs text-gray-500">
              These names appear on more than one device. Tables tell them apart by
              appending the record number (for example &ldquo;O836 · #1234&rdquo;);
              fixing the names in GIS is the lasting cleanup.
            </div>
            <div className="max-h-72 overflow-y-auto overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-white">
                  <tr className="border-b border-gray-200 text-left text-gray-500">
                    <th className="pb-1 pr-2">Name</th>
                    <th className="pb-1 pr-2 text-right">Devices</th>
                    <th className="pb-1">Records</th>
                  </tr>
                </thead>
                <tbody>
                  {dup.groups.map(g => (
                    <tr key={g.name} className="border-b border-gray-50">
                      <td className="py-1 pr-2 font-medium text-gray-700">{g.name}</td>
                      <td className="py-1 pr-2 text-right tabular-nums text-gray-700">{g.count}</td>
                      <td className="py-1 text-gray-500">
                        {g.device_ids.map(id => `#${id}`).join(', ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* GIS fed-by check (spec 2026-09-02 Phase 4.4). What the map data says
          feeds each transformer and cabinet, versus what the protection run
          computed. Read-only: the worklist and the field decide, not this tab. */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700">GIS fed-by check</h3>
          {fed && fed.off_chain + fed.other_feeder > 0 && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700">
              {fed.off_chain + fed.other_feeder} mismatches
            </span>
          )}
        </div>
        {!fed || fed.run_id == null ? (
          <div className="p-2 text-sm text-gray-400">
            No completed protection run yet, so there is nothing to compare the map
            data against.
          </div>
        ) : fed.total === 0 ? (
          <div className="p-2 text-sm text-gray-400">
            Protection run {fed.run_id} recorded no fed-by checks (it ran before this
            check existed).
          </div>
        ) : (
          <>
            <div className="mb-3 text-xs text-gray-500">
              The map data records what feeds each transformer and cabinet. These
              counts compare that record with the chain the model computed on
              protection run {fed.run_id}. Work the mismatches from the Fed-by
              mismatches list on the GIS queue&apos;s Worklists tab.
            </div>
            <div className="grid grid-cols-2 gap-2 text-center sm:grid-cols-3">
              {(
                [
                  ['confirmed', 'Agrees', 'text-green-700'],
                  ['in_chain', 'Further upstream', 'text-gray-700'],
                  // Derived (confirmed + in_chain): the pointer names a device
                  // that is on the computed chain somewhere. The strict
                  // "Agrees" count is the spec's target; this sits beside it.
                  ['consistent', 'Agrees or further upstream', 'text-green-700'],
                  ['off_chain', 'Not on the path', 'text-amber-700'],
                  ['other_feeder', 'Another circuit', 'text-amber-700'],
                  // "No computed chain" is the mandated wording for `islanded`
                  // (it also covers unplaced / unsnapped entities, which are
                  // not electrically islanded at all).
                  ['islanded', 'No computed chain', 'text-gray-700'],
                  ['unresolved', 'No usable record', 'text-gray-500'],
                  ['sub_source', 'Fed from the substation', 'text-gray-500'],
                  ['total', 'Records checked', 'text-gray-700'],
                ] as const
              ).map(([key, label, tone]) => (
                <div key={key} className="rounded-lg border border-gray-200 p-2">
                  <div className={`text-lg font-bold ${tone}`}>{fed[key]}</div>
                  <div className="mt-0.5 text-[11px] text-gray-500">{label}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Meter link evidence (spec 2026-09-02 section 6 Phase 5.3). How the
          linked transformer compares with the other three evidence sources.
          Absent on an older cached rollup that predates this field -- render
          nothing rather than a misleading empty state. */}
      {le && (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <h3 className="mb-2 text-sm font-semibold text-gray-700">Meter link evidence</h3>
          <div className="grid grid-cols-2 gap-2 text-center sm:grid-cols-5">
            <div>
              <div className="text-lg font-bold text-gray-800">{le.unlinked_meters}</div>
              <div className="text-[11px] text-gray-500">Unlinked meters (system-wide)</div>
            </div>
            <div>
              <div className="text-lg font-bold text-gray-800">{le.spatial_fallback_meters}</div>
              <div className="text-[11px] text-gray-500">Spatial fallback</div>
            </div>
            <div>
              <div className="text-lg font-bold text-gray-800">{le.secondary_disagreements}</div>
              <div className="text-[11px] text-gray-500">Secondary disagrees</div>
            </div>
            <div>
              <div className="text-lg font-bold text-gray-800">
                {le.pole_evidence_disagreements}
              </div>
              <div className="text-[11px] text-gray-500">Pole disagrees</div>
            </div>
            <div>
              <div className="text-lg font-bold text-gray-800">{le.fed_by_off_chain}</div>
              <div className="text-[11px] text-gray-500">Fed-by off chain</div>
            </div>
          </div>
          <div className="mt-2 text-xs text-gray-500">
            How the linked transformer compares with the three other sources of
            evidence on this feeder. The unlinked count is system-wide: a meter
            with no transformer belongs to no feeder yet. The worklists on the
            GIS queue page list each row.
          </div>
        </div>
      )}

      {/* Normally-open boundary worklist (spec §7 item 6). */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700">Normally-open boundary validation</h3>
          <div className="flex items-center gap-2 text-xs">
            <span className="rounded-full bg-green-100 px-2 py-0.5 text-green-700">
              {nb.boundary_ok} boundary-ok
            </span>
            <span className="rounded-full bg-red-100 px-2 py-0.5 text-red-700">
              {nb.interior_defect} interior defect
            </span>
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-600">
              {nb.unplaceable} unplaceable
            </span>
          </div>
        </div>

        {nb.items.length === 0 ? (
          <div className="p-2 text-sm text-gray-400">
            No normally-open devices on this feeder.
          </div>
        ) : (
          <div className="max-h-72 overflow-y-auto overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-gray-200 text-left text-gray-500">
                  <th className="pb-1 pr-2">Device</th>
                  <th className="pb-1 pr-2">Classification</th>
                  <th className="pb-1">Reason</th>
                </tr>
              </thead>
              <tbody>
                {nb.items.map(it => (
                  <tr
                    key={it.device_id}
                    className={`border-b border-gray-50 ${
                      NO_BOUNDARY_ROW_STYLES[it.classification] ?? 'text-gray-700'
                    }`}
                  >
                    <td className="py-1 pr-2 font-medium">{it.name ?? `#${it.device_id}`}</td>
                    <td className="py-1 pr-2">
                      {it.classification === 'interior_defect' ? (
                        <span className="rounded bg-red-200 px-1.5 py-0.5 font-semibold text-red-900">
                          ⚠ Interior defect
                        </span>
                      ) : (
                        <span className="capitalize">{it.classification.replace(/_/g, ' ')}</span>
                      )}
                    </td>
                    <td className="py-1">{it.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Screening disclaimer — PE / field verification required. */}
        <div className="mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] leading-snug text-amber-800">
          {nb.disclaimer}
        </div>
      </div>
    </div>
  )
}
